import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseSmoothProgressDisplayOptions {
  tickMs?: number;
  minStep?: number;
  easing?: number;
}

/**
 * Monotonic progress display: eases toward the latest target without jumping backward.
 */
export function useSmoothProgressDisplay(options: UseSmoothProgressDisplayOptions = {}) {
  const { tickMs = 100, minStep = 0.25, easing = 0.14 } = options;

  const [displayed, setDisplayed] = useState(0);
  const targetRef = useRef(0);
  const displayedRef = useRef(0);

  const setTarget = useCallback((value: number, opts?: { force?: boolean }) => {
    const clamped = Math.max(0, Math.min(100, value));
    if (opts?.force) {
      targetRef.current = clamped;
      displayedRef.current = clamped;
      setDisplayed(clamped);
      return;
    }
    targetRef.current = Math.max(targetRef.current, clamped);
  }, []);

  const reset = useCallback(() => {
    targetRef.current = 0;
    displayedRef.current = 0;
    setDisplayed(0);
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      const target = targetRef.current;
      const current = displayedRef.current;
      if (Math.abs(target - current) < 0.05) {
        if (current !== target) {
          displayedRef.current = target;
          setDisplayed(target);
        }
        return;
      }

      const gap = target - current;
      const step = Math.max(minStep, Math.abs(gap) * easing);
      const next = gap > 0 ? Math.min(target, current + step) : Math.max(target, current - step);
      displayedRef.current = next;
      setDisplayed(next);
    }, tickMs);

    return () => clearInterval(id);
  }, [tickMs, minStep, easing]);

  return { displayed, setTarget, reset };
}
